/// <amd-module name="Environments/EnvironmentDetails/Stores/EnvironmentStore" />
import { ActionsHubManager } from "Environments/Flux/ActionsHubManager";
import { EnvironmentActions } from "Environments/EnvironmentDetails/Actions/EnvironmentActions";
import { StoreBase, IStoreState } from "Environments/Flux/Store"

import { localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IEnvironmentObject, IEnvironmentResourceObject } from "Environments/Client/EnvironmentContracts";

export interface IEnvironmentStoreState extends IStoreState {
    environment: IEnvironmentObject;
}

export class EnvironmentStore extends StoreBase {

    public static getKey(): string {
        return "environment-store";
    }
    
    constructor(environment: IEnvironmentObject) {
        super("EnvironmentStoreChangedEvent");
        this._state.environment = environment;
    }

    public initialize(instanceId: string): void {
        super.initialize(instanceId);
        
        this._actionsHub = ActionsHubManager.GetActionsHub<EnvironmentActions>(EnvironmentActions, instanceId);
        this._actionsHub.updateEnvironment.addListener(this._handleUpdateEnvironment);
        this._actionsHub.setEnvironment.addListener(this._handleSetEnvironment);
        this._actionsHub.deleteResource.addListener(this._handleDeleteResource);
        this._actionsHub.updateResources.addListener(this._handleUpdateResources);
    }
    
    protected disposeInternal(): void {
        this._actionsHub.updateEnvironment.removeListener(this._handleUpdateEnvironment);
        this._actionsHub.setEnvironment.removeListener(this._handleSetEnvironment);
        this._actionsHub.deleteResource.removeListener(this._handleDeleteResource);
        this._actionsHub.updateResources.removeListener(this._handleUpdateResources);
    }

    public getState(): IEnvironmentStoreState {
        return this._state;
    }

    public getEnvironment(): IEnvironmentObject {
        return this._state.environment;
    }

    public getResources(): IEnvironmentResourceObject[] {
        return this._state.environment && this._state.environment.resources ? this._state.environment.resources : [];
    }

    public getResource(id: number): IEnvironmentResourceObject | undefined {
        return this.getResources().find((resource: IEnvironmentResourceObject) => { return resource.id === id; });
    }

    private _handleUpdateEnvironment = (environment: IEnvironmentObject) => {
        if(this._state.environment) {
            environment.resources = this._state.environment.resources;
            this._state.environment = environment;
            this.emitChanged();
        }
    }

    private _handleSetEnvironment = (environment: IEnvironmentObject) => {
        this._setStateAndEmit(environment);
    }

    private _handleDeleteResource = (resourceId: number) => {
        if (this._state.environment && this._state.environment.resources) {
            this._state.environment.resources = this._state.environment.resources.filter((resource: IEnvironmentResourceObject) => {
                return resource.id !== resourceId;
            });

            this.emitChanged();
        }
    }

    private _handleUpdateResources = (resources: IEnvironmentResourceObject[]) => {
        const environment = this._state.environment;
        environment.resources = resources;
        this._setStateAndEmit(environment);
    }

    private _setStateAndEmit(environment: IEnvironmentObject): void {
        if (environment) {
            environment.resources = environment.resources ? environment.resources.sort((a: IEnvironmentResourceObject, b: IEnvironmentResourceObject) => {
                return localeIgnoreCaseComparer(a.name, b.name)
            }) : [];

            this._state.environment = environment;
            this.emitChanged();
        }
    }

    private _actionsHub: EnvironmentActions;
    private _state: IEnvironmentStoreState = {} as IEnvironmentStoreState;
}
